import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { ReactComponent as PlusIcon } from "../../../assets/global-icons/plus.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  addContact,
  removeContact,
  updateContact,
  updateWorkOrderForm,
} from "../../../store/workOrder/workOrderSlice";
import { useQuery } from "@tanstack/react-query";
import { fetchTeamMembersByTeamIds } from "../../../services/api/teams";
import useAuth from "../../../hooks/useAuth";
import BaseSwitch from "../../../components/ui/BaseSwitch";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import PhoneInput from "react-phone-input-2";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import FieldLabel from "../../../components/ui/FieldLabel";
import { serviceDesk } from "../../../utils/variables";
import { UserType } from "../../../utils/enum";
import { normalizePhoneNumber, formatPhoneNumber } from "../../../utils/helpers";

const contactFormat = {
  id: Date.now(),
  name: "",
  position: "",
  note: "",
  email: "",
  position: "",
};

const positionsOptions = [
  {
    label: "Project Manager",
    value: "Project Manager",
  },
  {
    label: "Location Contact",
    value: "Location Contact",
  },
  {
    label: "Resource Coordinator",
    value: "Resource Coordinator",
  },
  {
    label: "Emergency Contact",
    value: "Emergency Contact",
  },
  {
    label: "Techinal Help",
    value: "Techinal Help",
  },
  {
    label: "Check-in / Check-out",
    value: "Check-in / Check-out",
  },
];

const ContactsForm = ({ open, isEditMode = false, workOrderData = {}, onHydrate }) => {
  const dispatch = useDispatch();
  const { auth, authRole } = useAuth();
  // Customers raise a reduced ticket: the internal Operations Service Desk and
  // Work Order Manager (a company user) are not relevant to them, so hide both.
  const isCustomer = authRole === UserType.CUSTOMER;
  const contact = useSelector((state) => state.workOrder.scheduling.contacts);
  const workOrderStatus = useSelector((state) => state.workOrder.status);
  // The Work Order Manager is picked from the members of the teams selected for this
  // work order (Service Delivery + Operations), not every company user.
  const teamId = useSelector((state) => state.workOrder.summary.overview.teamId);
  const secondaryTeamId = useSelector((state) => state.workOrder.summary.overview.secondaryTeamId);
  const selectedTeamIds = [teamId, secondaryTeamId].filter(Boolean);
  // A client reviewing a customer-raised ticket becomes the default manager (editable),
  // so the field is shown, always enabled, and pre-filled with the publishing client.
  const isPendingApprovalReview =
    isEditMode && workOrderStatus === "PENDING_APPROVAL" && !isCustomer;
  const prepopulated = useRef(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  const {
    isLoading: teamMembersLoading,
    error: teamMembersLoadingError,
    data: teamMembersData,
  } = useQuery({
    queryKey: ["workOrderTeamMembers", selectedTeamIds],
    queryFn: () => fetchTeamMembersByTeamIds(selectedTeamIds),
    enabled: selectedTeamIds.length > 0,
  });

  const formattedCompanyUsers = teamMembersData?.data?.map((user) => {
    return {
      value: user?.id,
      label: `${user.firstName} ${user.lastName}`,
    };
  });

  useEffect(() => {
    // Hydrate as soon as the contacts have arrived, empty list included — a work order
    // with no contacts yet still has a baseline ("nothing"), which is what keeps Save
    // Changes disabled until the user adds one.
    if (isEditMode && Array.isArray(workOrderData?.contacts) && !prepopulated.current) {
      // Default the manager to the publishing client when reviewing a customer ticket
      // that has none set yet; otherwise keep whatever the work order already had.
      const defaultManagerId =
        workOrderData?.workOrderManager?.id ||
        (isPendingApprovalReview ? auth?.user?.id : null) ||
        null;
      const hydratedContacts = {
        contacts: workOrderData.contacts,
        workOrderManagerId: defaultManagerId,
      };
      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: { contacts: hydratedContacts },
        }),
      );
      // Baseline for the edit modal's Save Changes button — see useSectionChanges.
      onHydrate?.(hydratedContacts);
      if (defaultManagerId) {
        setIsContactFormOpen(true);
      }

      prepopulated.current = true;
    }
  }, [isEditMode, workOrderData, dispatch, isPendingApprovalReview, auth]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          contacts: {
            [name]: value,
          },
        },
      }),
    );
  };

  const handleAddContact = () => {
    dispatch(addContact());
  };

  const handleContactChange = (index, event) => {
    const { name, value } = event.target;
    dispatch(updateContact({ index, field: name, value }));
  };

  const handleRemoveContact = (id) => {
    dispatch(removeContact(id));
  };

  return (
    <BaseCard className="pt-4 pb-6 lg:px-16 px-5 mt-2 rounded-lg" elevation={1}>
      <div className="border-bottom mb-4 flex items-center">
        <h6 className="mb-2 text-dark">Contacts</h6>
      </div>

      {!isCustomer && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-12 mt-6">
          {serviceDesk && (
            <div className="bg-light pt-3 pb-6 px-4 rounded-lg">
              <p className="text-lg font-normal mb-1">{serviceDesk.name}</p>
              <p className="mb-1">{serviceDesk.position}</p>
              <p className="text-primary mb-1">{formatPhoneNumber(serviceDesk.phone)}</p>
              <p className="text-primary mb-1 break-all">{serviceDesk.email}</p>
              <p className="mb-1 text-sm">Note: {serviceDesk.note}</p>
            </div>
          )}
        </div>
      )}

      {!isCustomer && (
        <div className="col-span-2 mt-4">
          <div className="flex items-center gap-3 mb-2">
            <p className="text-sm text-dark">Work Order Manager</p>

            <div className="flex items-center gap-2">
              <BaseCheckbox
                secondary={true}
                checked={isContactFormOpen}
                onChange={() => setIsContactFormOpen(!isContactFormOpen)}
              />
            </div>
          </div>
          <BaseAutoSelect
            fullHeight={false}
            fullWidth
            labelShrink={false}
            required={true}
            id="workOrderManagerId"
            name="workOrderManagerId"
            value={contact?.workOrderManagerId}
            onChange={handleChange}
            isDefaultField={false}
            options={formattedCompanyUsers}
            disabled={!isContactFormOpen} // 🔹 disabled when unchecked
          />
        </div>
      )}
      <div className="flex items-center cursor-pointer mt-5" onClick={handleAddContact}>
        <PlusIcon />
        <p className="text-secondary ml-2">Add Contact</p>
      </div>

      {contact?.contacts.map((contact, index) => (
        <div
          className="col-span-2 grid grid-cols-2 gap-y-4 bg-[#F6F6F6] p-5 rounded-lg mt-3"
          key={index}
        >
          <div className="col-span-2 text-right">
            <div
              className="lg:p-2 cursor-pointer inline-block"
              onClick={() => handleRemoveContact(contact?.id)}
            >
              <i className="fa-regular fa-trash-can"></i>
            </div>
          </div>
          <div className="lg:mr-10 lg:col-span-1 col-span-2">
            <BaseTextField
              fullWidth
              fullHeight={false}
              id="name"
              name="name"
              label="Name or Description"
              optional={false}
              requirement="required"
              labelShrink={false}
              className="col-span-4"
              onChange={(e) => handleContactChange(index, e)}
              isDefaultField={false}
              value={contact.name}
              inputProps={{ maxLength: 50 }}
            />
          </div>

          <div className="lg:col-span-1 col-span-2">
            <BaseAutoSelect
              fullHeight={false}
              fullWidth
              label="Title"
              labelShrink={false}
              required={true}
              requirement="required"
              options={positionsOptions}
              name="position"
              onChange={(e) => handleContactChange(index, e)}
              isDefaultField={false}
              value={contact.position}
            />
          </div>
          <div className="lg:mr-10 lg:col-span-1 col-span-2">
            <FieldLabel
              label="Phone"
              htmlFor="phoneNumber"
              requirement="required"
              fullHeight={false}
            />

            <div className="relative w-full">
              <PhoneInput
                inputClass="!w-full !h-[42px] !pl-12 !pr-3 !text-sm !font-light !text-black !bg-white !border !rounded-md !shadow-md focus:!outline-none focus:!ring-1 focus:!ring-orange-400"
                buttonClass="!border-none !bg-transparent"
                containerClass="!w-full"
                specialLabel=""
                country={"au"}
                value={contact.phoneNumber}
                onChange={(value, country) => {
                  const event = {
                    target: {
                      name: "phoneNumber",
                      value: normalizePhoneNumber(value, country),
                    },
                  };
                  handleContactChange(index, event);
                }}
                inputProps={{
                  name: "phoneNumber",
                  id: "phoneNumber",
                  type: "tel",
                }}
              />
            </div>
          </div>

          <div className="lg:col-span-1 col-span-2">
            <BaseTextField
              fullHeight={false}
              fullWidth
              label="Email"
              labelShrink={false}
              required={false}
              options={[]}
              name="email"
              onChange={(e) => handleContactChange(index, e)}
              isDefaultField={false}
              value={contact.email}
              type="email"
            />
          </div>

          <div className="col-span-2">
            <BaseTextField
              fullWidth
              fullHeight={false}
              id="note"
              name="note"
              label="Note"
              required={false}
              labelShrink={false}
              className="col-span-4"
              onChange={(e) => handleContactChange(index, e)}
              isDefaultField={false}
              value={contact.note}
              inputProps={{ maxLength: 100 }}
            />
          </div>
        </div>
      ))}
    </BaseCard>
  );
};

export default ContactsForm;
